import { useCallback, useMemo, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useProjectDetail } from '@hooks/projects/detail/useProjectDetail';
import { getRealtimeSocket, subscribeProject, unsubscribeProject } from '@services/realtime.socket';
import { useCanCheck } from '@/ability/helpers';
import { PROJECT_BUGS_VIEW } from '@/ability';
import {
    getProjectBugs,
    createProjectBug,
    createProjectBugsBatch,
    updateProjectBug,
    buildCreateProjectBugsBatchFormData,
    getProjectBugDiscussions,
    createProjectBugDiscussion,
    updateProjectBugDiscussion,
    deleteProjectBugDiscussion,
    addProjectTaskComment,
} from '@services/projects.service';
import { DISCUSSION_SOCKET_EVENTS } from './discussion/constants/discussion.constants';
import { message } from 'antd';
import { buildBugsHierarchy, buildFlatSprintsFromBugs } from '@hooks/projects/projectHelpers';
import { invalidateProjectQueries } from '@hooks/projects/projectQueryInvalidation';

export const PROJECT_BUGS_QUERY_KEY = (projectId) => ['projects', projectId, 'bugs'];
export const BUG_DISCUSSIONS_QUERY_KEY = (projectId, bugId) => ['projects', projectId, 'bugs', bugId, 'discussion'];

/**
 * @param {string} projectId
 * @param {{ enabled?: boolean }} options - When false, bugs list is not fetched (e.g. QA tab only needs createBug mutation).
 */
export function useProjectBugs(projectId, options = {}) {
    const queryClient = useQueryClient();
    const can = useCanCheck();
    const canView = can(PROJECT_BUGS_VIEW);

    const { data: projectFromQuery } = useProjectDetail(projectId, { enabled: !options.project && !!projectId });
    const project = options.project || projectFromQuery;
    const useTasksEnabled = project?.capabilities?.useTasks !== false;
    const enabled = options.enabled !== false && !!projectId && canView && useTasksEnabled;

    const {
        data: bugs = [],
        isLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey: PROJECT_BUGS_QUERY_KEY(projectId),
        queryFn: () => getProjectBugs(projectId),
        enabled,
    });

    const flatSprints = useMemo(() => buildFlatSprintsFromBugs(Array.isArray(bugs) ? bugs : []), [bugs]);

    const createMutation = useMutation({
        mutationFn: (body) => {
            if (Array.isArray(body.bugs) && body.bugs.length > 0 && body.taskId != null) {
                const formData = buildCreateProjectBugsBatchFormData(String(body.taskId), body.bugs);
                return createProjectBugsBatch(projectId, formData);
            }
            const files = body.files;
            const linkAttachments = Array.isArray(body.attachments) ? body.attachments : [];
            if (files?.length) {
                const formData = new FormData();
                formData.append('taskId', String(body.taskId));
                formData.append('severity', String(body.severity ?? 'high'));
                formData.append('description', JSON.stringify(body.description ?? {}));
                if (Array.isArray(body.assignedToIds) && body.assignedToIds.length > 0) {
                    formData.append('assignedToIds', JSON.stringify(body.assignedToIds));
                }
                if (linkAttachments.length > 0) {
                    formData.append('attachmentUrls', JSON.stringify(linkAttachments));
                }
                files.forEach((file) => formData.append('files', file));
                return createProjectBug(projectId, formData);
            }
            const { files: _f, attachments: _a, ...rest } = body;
            return createProjectBug(projectId, {
                ...rest,
                ...(linkAttachments.length > 0 && { attachments: linkAttachments }),
            });
        },
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
            void queryClient.refetchQueries({ queryKey: PROJECT_BUGS_QUERY_KEY(projectId), type: 'all' });
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ bugId, body }) => updateProjectBug(projectId, bugId, body),
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
        },
    });

    const createBug = (body, callbacks = {}) => {
        createMutation.mutate(body, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    const createBugAsync = (body) => createMutation.mutateAsync(body);

    const updateBug = (bugId, body, callbacks = {}) => {
        updateMutation.mutate(
            { bugId, body },
            {
                onSuccess: callbacks.onSuccess,
                onError: callbacks.onError,
            },
        );
    };

    return {
        bugs: Array.isArray(bugs) ? bugs : [],
        flatSprints,
        isLoading,
        isError,
        refetch,
        createBug,
        createBugAsync,
        createBugPending: createMutation.isPending,
        updateBug,
        updateBugPending: updateMutation.isPending,
        canView,
    };
}

// =========================================================
// Bugs tab panel state
// =========================================================

/**
 * Panel state for the Bugs tab. Hierarchy view: open by task or by bug.
 * openPanel(sprint, task, bug?) — bug optional; when provided, that bug is highlighted in the panel.
 */
export function useProjectBugsPanel({ updateBug, updateBugPending } = {}) {
    const [selectedTask, setSelectedTask] = useState(null);
    const [selectedBug, setSelectedBug] = useState(null);

    const openPanel = useCallback((sprint, task, bug = null) => {
        setSelectedTask({ sprint, task });
        setSelectedBug(bug ?? null);
    }, []);
    const closePanel = useCallback(() => {
        setSelectedTask(null);
        setSelectedBug(null);
    }, []);

    const selectedBugs = selectedTask?.task?.bugs ?? [];

    const changeSelectedBugStatus = useCallback(
        (nextStatus) => {
            if (!selectedBug?.id || !updateBug) return;
            const bugId = selectedBug.id;
            updateBug(bugId, { status: nextStatus }, {
                onSuccess: () => {
                    setSelectedBug((prev) => (prev && prev.id === bugId ? { ...prev, status: nextStatus } : prev));
                },
                onError: (err) => {
                    message.error(err?.response?.data?.message || err?.message || 'Failed to update bug status');
                },
            });
        },
        [selectedBug, updateBug],
    );

    return {
        selectedTask,
        selectedBug,
        selectedBugs,
        openPanel,
        closePanel,
        changeSelectedBugStatus,
        changeStatusPending: !!updateBugPending,
    };
}

// =========================================================
// Bug discussions
// =========================================================

/** Sort by createdAt descending (newest first). */
function sortByNewestFirst(items) {
    if (!Array.isArray(items) || items.length === 0) return items;
    return [...items].sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
    });
}

function getBugIdFromSocketPayload(payload) {
    if (!payload) return null;
    return payload.bugId ?? payload.bug_id ?? payload?.bug?.id ?? null;
}

/**
 * Bug discussion: list of posts, create post. Same payload style as project discussion
 * (JSON when no files, FormData with content + attachmentUrls + files when files attached).
 */
export function useBugDiscussions(projectId, bugId) {
    const queryClient = useQueryClient();
    const can = useCanCheck();
    const canViewBugs = can(PROJECT_BUGS_VIEW);

    useEffect(() => {
        if (!projectId || !bugId || !canViewBugs) return;
        const socket = getRealtimeSocket();
        if (!socket) return;

        const handleNewPost = (data) => {
            const targetBugId = getBugIdFromSocketPayload(data);
            if (targetBugId == null || String(targetBugId) !== String(bugId)) return;
            const msg = data.message;
            if (!msg) return;

            queryClient.setQueryData(BUG_DISCUSSIONS_QUERY_KEY(projectId, bugId), (prev) => {
                const list = Array.isArray(prev) ? prev : [];
                if (list.some((p) => (p.id ?? p.discussionId) === msg.id)) return prev;
                return sortByNewestFirst([...list, msg]);
            });
        };

        const handleUpdatePost = (data) => {
            const targetBugId = getBugIdFromSocketPayload(data);
            if (targetBugId == null || String(targetBugId) !== String(bugId)) return;
            const msg = data.message;
            if (!msg) return;

            queryClient.setQueryData(BUG_DISCUSSIONS_QUERY_KEY(projectId, bugId), (prev) => {
                const list = Array.isArray(prev) ? prev : [];
                return list.map((p) => ((p.id ?? p.discussionId) === msg.id ? { ...p, ...msg } : p));
            });
        };

        const handleDeletePost = (data) => {
            const targetBugId = getBugIdFromSocketPayload(data);
            if (targetBugId == null || String(targetBugId) !== String(bugId)) return;
            const id = data.messageId;
            if (id == null) return;

            queryClient.setQueryData(BUG_DISCUSSIONS_QUERY_KEY(projectId, bugId), (prev) => {
                const list = Array.isArray(prev) ? prev : [];
                return list.filter((p) => (p.id ?? p.discussionId) !== id);
            });
        };

        socket.on(DISCUSSION_SOCKET_EVENTS.BUG_NEW, handleNewPost);
        socket.on(DISCUSSION_SOCKET_EVENTS.BUG_UPDATED, handleUpdatePost);
        socket.on(DISCUSSION_SOCKET_EVENTS.BUG_DELETED, handleDeletePost);
        subscribeProject(projectId);

        return () => {
            socket.off(DISCUSSION_SOCKET_EVENTS.BUG_NEW, handleNewPost);
            socket.off(DISCUSSION_SOCKET_EVENTS.BUG_UPDATED, handleUpdatePost);
            socket.off(DISCUSSION_SOCKET_EVENTS.BUG_DELETED, handleDeletePost);
            unsubscribeProject(projectId);
        };
    }, [projectId, bugId, queryClient, canViewBugs]);

    const { data: project } = useProjectDetail(projectId, { enabled: !!projectId });
    const useTasksEnabled = project?.capabilities?.useTasks !== false;

    const {
        data: discussions = [],
        isLoading,
        isError,
        refetch,
    } = useQuery({
        queryKey: BUG_DISCUSSIONS_QUERY_KEY(projectId, bugId),
        queryFn: () => getProjectBugDiscussions(projectId, bugId),
        enabled: !!projectId && !!bugId && canViewBugs && useTasksEnabled,
        // Bug discussion stays fresh via sockets; prevent refetching on every panel open.
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        // Fetch on first open and when stale; skip noisy remount refetches while fresh.
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const discussionsNewestFirst = useMemo(
        () => sortByNewestFirst(Array.isArray(discussions) ? discussions : []),
        [discussions],
    );

    const createMutation = useMutation({
        mutationFn: (payload) => createProjectBugDiscussion(projectId, bugId, payload),
        onSuccess: (data) => {
            const post = data?.data ?? data;
            if (post && (post.id != null || post.discussionId != null)) {
                queryClient.setQueryData(BUG_DISCUSSIONS_QUERY_KEY(projectId, bugId), (prev) => {
                    const list = Array.isArray(prev) ? prev : [];
                    const id = post.id ?? post.discussionId;
                    if (list.some((p) => (p.id ?? p.discussionId) === id)) return prev;
                    return sortByNewestFirst([...list, post]);
                });
            }
        },
    });

    const createPost = (body, callbacks = {}) => {
        const { content, attachments = [], files } = body;
        const linkAttachments = Array.isArray(attachments) ? attachments : [];
        let payload;
        if (files?.length) {
            const formData = new FormData();
            formData.append('content', typeof content === 'string' ? content : '');
            if (linkAttachments.length > 0) {
                formData.append('attachmentUrls', JSON.stringify(linkAttachments));
            }
            files.forEach((file) => formData.append('files', file));
            payload = formData;
        } else {
            payload = {
                content: typeof content === 'string' ? content : '',
                attachments: linkAttachments,
            };
        }
        createMutation.mutate(payload, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    const editMutation = useMutation({
        mutationFn: ({ discussionId, payload }) => updateProjectBugDiscussion(projectId, bugId, discussionId, payload),
        onSuccess: (data, { discussionId }) => {
            const post = data?.data ?? data;
            queryClient.setQueryData(BUG_DISCUSSIONS_QUERY_KEY(projectId, bugId), (prev) => {
                const list = Array.isArray(prev) ? prev : [];
                return list.map((p) => ((p.id ?? p.discussionId) === discussionId ? { ...p, ...post } : p));
            });
        },
    });

    const editPost = (discussionId, body, callbacks = {}) => {
        const { content, attachments = [], files } = body;
        const linkAttachments = Array.isArray(attachments) ? attachments : [];
        let payload;
        if (files?.length) {
            const formData = new FormData();
            formData.append('content', typeof content === 'string' ? content : '');
            if (linkAttachments.length > 0) {
                formData.append('attachmentUrls', JSON.stringify(linkAttachments));
            }
            files.forEach((file) => formData.append('files', file));
            payload = formData;
        } else {
            payload = {
                content: typeof content === 'string' ? content : '',
                attachments: linkAttachments,
            };
        }
        editMutation.mutate({ discussionId, payload }, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    const deleteMutation = useMutation({
        mutationFn: (discussionId) => deleteProjectBugDiscussion(projectId, bugId, discussionId),
        onSuccess: (_, discussionId) => {
            queryClient.setQueryData(BUG_DISCUSSIONS_QUERY_KEY(projectId, bugId), (prev) => {
                const list = Array.isArray(prev) ? prev : [];
                return list.filter((p) => (p.id ?? p.discussionId) !== discussionId);
            });
        },
    });

    const deletePost = (discussionId, callbacks = {}) => {
        deleteMutation.mutate(discussionId, {
            onSuccess: callbacks.onSuccess,
            onError: callbacks.onError,
        });
    };

    return {
        discussions: discussionsNewestFirst,
        isLoading,
        isError,
        refetch,
        createPost,
        createPostPending: createMutation.isPending,
        editPost,
        editPostPending: editMutation.isPending,
        deletePost,
        deletePostPending: deleteMutation.isPending,
    };
}

// =========================================================
// Flow graph (React Flow)
// =========================================================

const NODE_WIDTH = 200;
const NODE_HEIGHT = 52;
/** Milestone card collapsed height so sprints/tasks sit below it. */
const MILESTONE_NODE_HEIGHT = 165;
/** Extra gap between milestone and first sprint row so cards don’t overlap. */
const MILESTONE_TO_SPRINT_GAP = 100;
/** Sprint card collapsed height; reserve plenty so card never overlaps task row. */
const SPRINT_NODE_HEIGHT = 200;
/** Clear gap between sprint card bottom and task row top. */
const SPRINT_TO_TASK_GAP = 140;
/** Task card collapsed height. */
const TASK_NODE_HEIGHT = 100;
/** Gap between task card and bug row. */
const TASK_TO_BUG_GAP = 80;
/** Bug card height. */
const BUG_NODE_HEIGHT = 90;
/** Gap between bug cards (same as task-to-bug gap). */
const BUG_TO_BUG_GAP = 80;
const LEVEL_GAP = 56;
const TASK_BRANCH_WIDTH = 240;
const SPRINT_BRANCH_WIDTH = 800; // horizontal spread per sprint when multiple sprints
const MILESTONE_BLOCK_WIDTH = 1100;

const ID = (type, id) => `${type}-${id}`;

export function buildBugsFlowGraph(hierarchy) {
    if (!Array.isArray(hierarchy) || hierarchy.length === 0) return { nodes: [], edges: [] };

    const nodes = [];
    const edges = [];

    hierarchy.forEach(({ milestone, sprints }, milestoneIndex) => {
        const baseX = milestoneIndex * MILESTONE_BLOCK_WIDTH;
        const mId = ID('m', milestone?.id);
        const numSprints = sprints.length;

        const milestoneCenterX =
            numSprints === 0
                ? baseX
                : numSprints === 1
                    ? sprints[0]?.tasks?.length
                        ? baseX + ((sprints[0].tasks.length - 1) * TASK_BRANCH_WIDTH) / 2
                        : baseX
                    : baseX + ((numSprints - 1) * SPRINT_BRANCH_WIDTH) / 2;

        nodes.push({
            id: mId,
            type: 'milestone',
            position: { x: milestoneCenterX, y: 0 },
            data: {
                label: milestone?.name ?? 'Milestone',
                meta: milestone?.dueDate ? new Date(milestone.dueDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null,
                raw: milestone,
            },
            width: NODE_WIDTH,
            height: MILESTONE_NODE_HEIGHT,
            style: { width: NODE_WIDTH, height: MILESTONE_NODE_HEIGHT },
        });

        let sprintY = MILESTONE_NODE_HEIGHT + MILESTONE_TO_SPRINT_GAP;

        sprints.forEach(({ sprint, tasks }, sprintIndex) => {
            const sId = ID('s', sprint?.id);
            const numTasks = tasks.length;
            const taskRowY = sprintY + SPRINT_NODE_HEIGHT + SPRINT_TO_TASK_GAP;
            const sprintBaseX = numSprints === 1 ? baseX : baseX + sprintIndex * SPRINT_BRANCH_WIDTH;
            const sprintCenterX = numTasks > 0 ? sprintBaseX + ((numTasks - 1) * TASK_BRANCH_WIDTH) / 2 : sprintBaseX;

            edges.push({ id: `e-${mId}-${sId}`, source: mId, target: sId });
            nodes.push({
                id: sId,
                type: 'sprint',
                position: { x: sprintCenterX, y: sprintY },
                data: {
                    label: sprint?.name ?? 'Sprint',
                    meta: sprint?.startDate && sprint?.endDate
                        ? `${new Date(sprint.startDate).toLocaleDateString(undefined, { month: 'short' })} – ${new Date(sprint.endDate).toLocaleDateString(undefined, { month: 'short', year: '2-digit' })}`
                        : null,
                    raw: sprint,
                },
                width: NODE_WIDTH,
                height: SPRINT_NODE_HEIGHT,
                style: { width: NODE_WIDTH, height: SPRINT_NODE_HEIGHT },
            });

            tasks.forEach(({ task, bugs: taskBugs }, taskIndex) => {
                const tId = ID('t', task?.id);
                const taskWithBugs = { ...task, bugs: taskBugs ?? [] };
                const taskX = sprintBaseX + taskIndex * TASK_BRANCH_WIDTH;

                edges.push({ id: `e-${sId}-${tId}`, source: sId, target: tId });
                nodes.push({
                    id: tId,
                    type: 'task',
                    position: { x: taskX, y: taskRowY },
                    data: {
                        label: task?.title ?? task?.name ?? 'Task',
                        meta: (taskBugs?.length ?? 0) > 0 ? `${taskBugs.length} bug${taskBugs.length !== 1 ? 's' : ''}` : null,
                        raw: taskWithBugs,
                        sprint,
                        milestone,
                    },
                    width: NODE_WIDTH,
                    height: TASK_NODE_HEIGHT,
                    style: { width: NODE_WIDTH, height: TASK_NODE_HEIGHT },
                });

                let bugY = taskRowY + TASK_NODE_HEIGHT + TASK_TO_BUG_GAP;

                (taskBugs ?? []).forEach((bug, bugIndex) => {
                    const bId = ID('b', bug?.id);
                    edges.push({ id: `e-${tId}-${bId}`, source: tId, target: bId });
                    const bugLabel = bug?.title && bug?.title !== 'Untitled'
                        ? bug.title
                        : (bug?.description || 'Bug').replace(/<[^>]+>/g, ' ').trim().slice(0, 50) || 'Bug';
                    nodes.push({
                        id: bId,
                        type: 'bug',
                        position: { x: taskX, y: bugY },
                        data: {
                            label: bugLabel,
                            bugIndex,
                            meta: bug?.severity ?? bug?.status ?? null,
                            raw: bug,
                            task: taskWithBugs,
                            sprint,
                            milestone,
                        },
                        width: NODE_WIDTH,
                        height: BUG_NODE_HEIGHT,
                        style: { width: NODE_WIDTH, height: BUG_NODE_HEIGHT },
                    });
                    bugY += BUG_NODE_HEIGHT + BUG_TO_BUG_GAP;
                });
            });

            const maxBugsUnderSprint = Math.max(0, ...tasks.map((t) => (t.bugs ?? []).length));
            sprintY = taskRowY + TASK_NODE_HEIGHT + TASK_TO_BUG_GAP + maxBugsUnderSprint * (BUG_NODE_HEIGHT + BUG_TO_BUG_GAP) + LEVEL_GAP;
        });
    });

    return { nodes, edges };
}

/**
 * Hook: from flat bugs array, return React Flow nodes and edges for the hierarchy.
 */
export function useBugsFlowGraph(bugs) {
    const hierarchy = useMemo(() => buildBugsHierarchy(Array.isArray(bugs) ? bugs : []), [bugs]);
    return useMemo(() => buildBugsFlowGraph(hierarchy), [hierarchy]);
}

// =========================================================
// Task comments (QA)
// =========================================================

/**
 * Add a comment to a project task (QA tab). Invalidates all project queries so tab switch refetches.
 */
export function useAddProjectTaskComment(projectId) {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: ({ taskId, content }) => addProjectTaskComment(projectId, taskId, { content: content?.trim() || '' }),
        onSuccess: () => {
            invalidateProjectQueries(queryClient, projectId);
        },
    });

    const addComment = (taskId, content, callbacks = {}) => {
        mutation.mutate({ taskId, content }, { onSuccess: callbacks.onSuccess, onError: callbacks.onError });
    };

    return {
        addComment,
        isPending: mutation.isPending,
        isError: mutation.isError,
        error: mutation.error,
    };
}
